import { RedoClient } from "@redotech/redo-api-client";
import { WidgetAutocheck } from "@redotech/redo-model/widget";
import {
  getLocalStorageWithExpiry,
  setLocalStorageWithExpiry,
} from "@redotech/redo-web/utils/local-storage-wrapper";
import {
  getSessionStorageWithExpiry,
  setSessionStorageWithExpiry,
} from "@redotech/redo-web/utils/session-storage-wrapper";
import { COVERAGE_TREATMENT_ID_QUERY_PARAM } from "@redotech/redo-web/utils/shared-conf";
import {
  getAutocheck,
  ShopifyExtensionClient,
} from "./client/shopify-extension-client";
import { getLocalStorageIgnoreExpiry } from "./utils";

let widgetAutoCheckPromise: Promise<WidgetAutocheck> | null = null;

const AUTOCHECK_CACHE_KEY = "redo_autocheck";

function getAutocheckPromise(): Promise<WidgetAutocheck> {
  let foreignCurrency: string | undefined;
  let currencyExchangeRate: string | undefined;

  if (!!globalThis.Tapcart && !globalThis.Shopify) {
    foreignCurrency = globalThis.Tapcart.variables.cart.currency;
    currencyExchangeRate = undefined;
  } else {
    foreignCurrency = globalThis?.Shopify?.currency?.active;
    currencyExchangeRate = globalThis?.Shopify?.currency?.rate;
  }

  const queryString = location.search;
  const urlParams = new URLSearchParams(queryString);
  const urlID = urlParams.get(COVERAGE_TREATMENT_ID_QUERY_PARAM);
  const onPreview =
    urlParams.get("preview") === "true" ||
    getSessionStorageWithExpiry("redo_preview");

  setSessionStorageWithExpiry("redo_preview", onPreview, 1);
  const localStorageTreatmentId =
    getLocalStorageIgnoreExpiry("redo_treatment_id");

  const desiredTreatment = onPreview
    ? urlID || localStorageTreatmentId || undefined
    : undefined;

  const redoClient = new RedoClient(
    globalThis.redoStorefront?.env?.REDO_API_URL ?? process.env.REDO_API_URL!,
  );
  const client = new ShopifyExtensionClient(
    redoClient,
    globalThis.redoWidgetId,
    globalThis.Shopify?.shop ??
      globalThis.Shopify?.Checkout?.apiHost ??
      globalThis.redoStorefront?.storeUrl,
  );

  const customerId: string | null =
    (globalThis as any).ShopifyAnalytics?.meta.page.customerId || null;

  widgetAutoCheckPromise = getAutocheck(client, {
    customerId,
    foreignCurrency,
    currencyExchangeRate,
    desiredTreatment,
  });

  return widgetAutoCheckPromise;
}

async function refreshAutocheckCache() {
  const auto = widgetAutoCheckPromise
    ? await widgetAutoCheckPromise
    : await getAutocheckPromise();
  setLocalStorageWithExpiry(AUTOCHECK_CACHE_KEY, auto, 0.5);
}

export async function getAutocheckCache(): Promise<WidgetAutocheck> {
  const storedAutocheck = getLocalStorageWithExpiry(AUTOCHECK_CACHE_KEY);
  if (storedAutocheck) {
    void refreshAutocheckCache();
    return storedAutocheck;
  }

  if (widgetAutoCheckPromise === null) {
    widgetAutoCheckPromise = getAutocheckPromise();
  }

  try {
    const autocheck = await widgetAutoCheckPromise;
    setLocalStorageWithExpiry(AUTOCHECK_CACHE_KEY, autocheck, 0.1);

    return autocheck;
  } catch (error) {
    widgetAutoCheckPromise = null;
    throw error;
  }
}
